import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Checkbox } from 'antd';
import { useSelector } from 'react-redux';
import { NewFilterIcon } from '@/Components/Common/Icons';
import NewCustomButton, { ButtonTypes } from '@/Components/Common/NewCustomButton';
import {
  FilterBarContainer,
  FilterActions,
  FilterPopup,
  FilterSelect,
  ResetFiltersButton,
} from './FilterBar.styles';

const VALUE_SEPARATOR = ':';
// Tall enough for the largest filter list (Inventory) to show without scrolling.
const LIST_HEIGHT = 480;
const toValue = ({ groupKey, itemKey }) => `${groupKey}${VALUE_SEPARATOR}${itemKey}`;
const fromValue = (value) => {
  const [groupKey, itemKey] = value.split(VALUE_SEPARATOR);
  return { groupKey, itemKey };
};

const isSameFilter = (filter, groupKey, itemKey) =>
  filter.groupKey === groupKey && filter.itemKey === itemKey;

// Groups without `multiple` behave like a radio group: picking an option replaces that group's selection.
export const toggleFilterSelection = (selected, groups, groupKey, itemKey) => {
  if (selected.some((filter) => isSameFilter(filter, groupKey, itemKey))) {
    return selected.filter((filter) => !isSameFilter(filter, groupKey, itemKey));
  }
  const group = groups.find((g) => g.key === groupKey);
  const kept = group?.multiple ? selected : selected.filter((f) => f.groupKey !== groupKey);
  return [...kept, { groupKey, itemKey }];
};

// Drops persisted selections that are malformed or not offered to the current user.
export const sanitizeFilters = (saved, groups) =>
  Array.isArray(saved)
    ? saved.filter((filter) =>
        groups.some(
          (group) =>
            group.key === filter?.groupKey &&
            group.children.some((item) => item.key === filter?.itemKey)
        )
      )
    : [];

const FilterBar = ({ groups, selected, onChange, actions = {} }) => {
  const trans = useSelector(translator);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(selected);

  // With an Apply button, picks are buffered until Apply; otherwise they apply immediately.
  const usesDraft = Boolean(actions.apply);
  const current = usesDraft && open ? draft : selected;
  const showClear = actions.clearAll ?? true;
  const clearLabel = groups.some((group) => group.multiple) ? trans('CLEARALL') : trans('CLEAR');
  const hasActions = Boolean(showClear || actions.cancel || actions.apply);

  const toOptions = (group) =>
    group.children.map((item) => ({
      value: toValue({ groupKey: group.key, itemKey: item.key }),
      label: item.label,
    }));
  // Labelled groups (e.g. Inventory) are shown as option groups; unlabelled ones as a flat list.
  const options = groups.flatMap((group) =>
    group.label ? [{ label: group.label, options: toOptions(group) }] : toOptions(group)
  );

  // Select's own value bookkeeping is bypassed so single-select groups replace their option.
  const handleToggle = (value) => {
    const { groupKey, itemKey } = fromValue(value);
    const next = toggleFilterSelection(current, groups, groupKey, itemKey);
    if (usesDraft) {
      setDraft(next);
    } else {
      onChange(next);
    }
  };

  const handleOpenChange = (nextOpen) => {
    if (nextOpen) setDraft(selected);
    setOpen(nextOpen);
  };

  const handleClearAll = () => {
    onChange([]);
    setOpen(false);
  };

  const handleApply = () => {
    onChange(draft);
    setOpen(false);
  };

  const currentValues = current.map(toValue);

  const renderOption = (option) => {
    const isChecked = currentValues.includes(option.value);
    return (
      <Checkbox checked={isChecked} tabIndex={-1} onClick={(event) => event.preventDefault()}>
        <span>{option.label}</span>
      </Checkbox>
    );
  };

  const renderPopup = (menu) => (
    <FilterPopup $hasButtons={Boolean(actions.cancel || actions.apply)}>
      {menu}
      {hasActions && (
        <FilterActions onMouseDown={(event) => event.preventDefault()}>
          {showClear && (
            <ResetFiltersButton onClick={handleClearAll}>{clearLabel}</ResetFiltersButton>
          )}
          {actions.cancel && (
            <NewCustomButton
              className="cancel"
              label={trans('CANCEL')}
              onClick={() => setOpen(false)}
            />
          )}
          {actions.apply && (
            <NewCustomButton
              type={ButtonTypes.primary}
              label={trans('APPLY')}
              onClick={handleApply}
            />
          )}
        </FilterActions>
      )}
    </FilterPopup>
  );

  return (
    <FilterBarContainer>
      <FilterSelect
        mode="multiple"
        value={currentValues}
        options={options}
        open={open}
        onOpenChange={handleOpenChange}
        popupRender={renderPopup}
        optionRender={renderOption}
        menuItemSelectedIcon={null}
        onSelect={handleToggle}
        onDeselect={handleToggle}
        showSearch={false}
        placeholder={trans('FILTERS')}
        prefix={<NewFilterIcon />}
        maxTagCount={0}
        maxTagPlaceholder={() =>
          selected.length ? `${trans('FILTERS')} (${selected.length})` : trans('FILTERS')
        }
        popupMatchSelectWidth={false}
        listHeight={LIST_HEIGHT}
        aria-label={trans('FILTERS')}
      />
    </FilterBarContainer>
  );
};

FilterBar.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node,
      multiple: PropTypes.bool,
      children: PropTypes.arrayOf(
        PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.node })
      ).isRequired,
    })
  ).isRequired,
  selected: PropTypes.arrayOf(
    PropTypes.shape({ groupKey: PropTypes.string, itemKey: PropTypes.string })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  actions: PropTypes.shape({
    clearAll: PropTypes.bool,
    cancel: PropTypes.bool,
    apply: PropTypes.bool,
  }),
};

export default FilterBar;
